import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import * as Cesium from 'cesium';
import { polygon as turfPolygon, featureCollection } from '@turf/helpers';
import { union } from '@turf/union';
import { buildBufferHierarchy } from '../utils/cesiumHelpers';
import { createPlacemarkPin, createPlacemarkLabel, createMeasurementLine, createMeasurementLabel, createPathLine, createPolygonFill, createPolygonOutline, createGlowHighlight, createLabelPickerEntity, updateBillboardOpacity, updateLabelOpacity, updatePointOpacity, updateLineOpacity, updateLineAlpha, registerLineBaseOpacity, updatePolygonFillOpacity } from '../utils/cesiumEntityFactory';
import { createCircleEntity as createServerCircleShape, createCircleLabel as createServerCircleLabel, createCircleOutline as createServerCircleOutline, } from '../utils/measureCircleEntity';
import { slicePolylineByFraction } from '../utils/polylineInterpolation';
import { parseSketchGeometry } from '../utils/sketchGeometry';
import { isTerrainOn, VECTOR_LAYER_TAG, CONTOUR_HIGHLIGHT_ZINDEX } from '../utils/vectorLayerTerrainSync';
import { formatDistance, formatAreaFromSqMeters } from '../utils/units';
import { DEFAULT_LABEL_FONT } from '../components/LabelTool/types';
import { DEFAULT_POLYGON_FILL_OPACITY } from '../components/PolygonTool/types';
import { useCustomDataSource } from './common/useCustomDataSource';
import { withoutCreatingArtifacts } from './pendingArtifacts';
export const useServerArtifactRenderer = ({ viewer, folders: allFolders, artifactVisibility = {}, artifactOpacity = {}, folderVisibility = {}, derivedVisibility, suppressedArtifactIds, onPathArtifactClick, onPolygonArtifactClick, onStreamArtifactClick }) => {
    // A row still being created has no server id and its source drawing is
    // still on the canvas, so drawing it here would show it twice.
    const folders = useMemo(() => withoutCreatingArtifacts(allFolders), [allFolders]);
    const suppressedRef = useRef(suppressedArtifactIds !== null && suppressedArtifactIds !== void 0 ? suppressedArtifactIds : new Set());
    suppressedRef.current = suppressedArtifactIds !== null && suppressedArtifactIds !== void 0 ? suppressedArtifactIds : new Set();
    const dataSourceRef = useCustomDataSource(viewer, 'server-artifacts');
    const renderedArtifactsRef = useRef(new Map());
    const entityToArtifactRef = useRef(new Map());
    const parsedGeometryRef = useRef(new Map());
    const [selectedArtifact, setSelectedArtifact] = useState(null);
    const [poppedOutArtifacts, setPoppedOutArtifacts] = useState([]);
    // Saved labels exposed in ToolLabel shape so the shared DOM overlay
    // ([LabelMapOverlay]) can render them with the same font + halo logic
    // used for in-progress labels. The Cesium-side entity is still an
    // invisible picker that participates in click/drag.
    const [savedLabels, setSavedLabels] = useState(new Map());
    // The bookkeeping maps point at entities owned by the data source above, so
    // they have to be dropped on the same signal the data source is — otherwise
    // they pin detached entities (and their parsed geometry) after a viewer swap.
    useEffect(() => {
        return () => {
            renderedArtifactsRef.current.clear();
            entityToArtifactRef.current.clear();
            parsedGeometryRef.current.clear();
        };
    }, [viewer]);
    const getParsedGeometry = useCallback((artifact) => {
        const cached = parsedGeometryRef.current.get(artifact.id);
        if (cached && cached.json === artifact.geometryJson)
            return cached.parsed;
        const parsed = JSON.parse(artifact.geometryJson);
        parsedGeometryRef.current.set(artifact.id, { json: artifact.geometryJson, parsed });
        return parsed;
    }, []);
    const renderPlacemark = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            const position = Cesium.Cartesian3.fromDegrees(geometry.lng, geometry.lat);
            const scale = typeof geometry.scale === 'number' ? geometry.scale : 1.0;
            const pinEntity = createPlacemarkPin(dataSourceRef.current, position, artifact.color, artifact.icon, 1.0, viewer !== null && viewer !== void 0 ? viewer : undefined, scale);
            const labelEntity = createPlacemarkLabel(dataSourceRef.current, position, artifact.name, 1.0, scale);
            // NULL show_label = renderer default (visible). Explicit false hides.
            if (labelEntity && artifact.showLabel === false)
                labelEntity.show = false;
            return [pinEntity, labelEntity];
        }
        catch (_a) {
            return [];
        }
    }, [getParsedGeometry, viewer]);
    const renderLabel = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            // Use the persisted terrain height when available so the DOM
            // overlay re-projects to the same screen point it was anchored to
            // when saved. Older artifacts without height fall back to
            // ellipsoid level (the prior behaviour).
            const height = typeof geometry.height === 'number' ? geometry.height : 0;
            const position = Cesium.Cartesian3.fromDegrees(geometry.lng, geometry.lat, height);
            const text = (typeof geometry.text === 'string' && geometry.text) ? geometry.text : artifact.name;
            const entity = createLabelPickerEntity(dataSourceRef.current, position);
            const VALID_FONTS = new Set([
                'sans', 'serif', 'display', 'mono', 'hand',
                'verdana', 'trebuchet', 'palatino', 'garamond', 'brush'
            ]);
            const fontFamily = VALID_FONTS.has(geometry.fontFamily)
                ? geometry.fontFamily
                : DEFAULT_LABEL_FONT;
            const cornerRadius = (geometry.cornerRadius === 'square' || geometry.cornerRadius === 'pill')
                ? geometry.cornerRadius
                : 'rounded';
            const labelData = {
                id: artifact.id,
                name: artifact.name,
                position,
                text,
                color: artifact.color,
                size: typeof geometry.size === 'number' ? geometry.size : 16,
                // Back-compat: older labels stored `halo` (bool only). Newer
                // labels store `stroke`. Fall back if missing.
                stroke: typeof geometry.stroke === 'boolean'
                    ? geometry.stroke
                    : (geometry.halo !== false),
                bold: !!geometry.bold,
                italic: !!geometry.italic,
                background: (geometry.background === 'white' || geometry.background === 'black')
                    ? geometry.background
                    : 'none',
                cornerRadius,
                backgroundBorder: !!geometry.backgroundBorder,
                fontFamily,
                entity,
                referenceDistance: typeof geometry.referenceDistance === 'number' ? geometry.referenceDistance : undefined
            };
            setSavedLabels(prev => {
                const next = new Map(prev);
                next.set(artifact.id, labelData);
                return next;
            });
            return [entity];
        }
        catch (_a) {
            return [];
        }
    }, [getParsedGeometry]);
    const renderMeasurement = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            const startPosition = Cesium.Cartesian3.fromDegrees(geometry.startLng, geometry.startLat);
            const endPosition = Cesium.Cartesian3.fromDegrees(geometry.endLng, geometry.endLat);
            // Saved measurements render as a clean line only, no endpoint node markers
            // — mirrors renderPath. The handles belong to the live editing tool.
            const lineWidth = typeof geometry.lineWidth === 'number' ? geometry.lineWidth : undefined;
            const lineEntity = createMeasurementLine(dataSourceRef.current, [startPosition, endPosition], artifact.color, 1.0, lineWidth, viewer !== null && viewer !== void 0 ? viewer : undefined);
            const entities = [lineEntity];
            // Optional on-map label: distance + bearing, matching the live tool.
            const showLabel = typeof geometry.showLabel === 'boolean' ? geometry.showLabel : true;
            if (showLabel) {
                const distance = Cesium.Cartesian3.distance(startPosition, endPosition);
                const startCart = Cesium.Cartographic.fromCartesian(startPosition);
                const endCart = Cesium.Cartographic.fromCartesian(endPosition);
                const geodesic = new Cesium.EllipsoidGeodesic(startCart, endCart);
                const bearingDeg = ((Cesium.Math.toDegrees(geodesic.startHeading) % 360) + 360) % 360;
                const unit = ['m', 'km', 'ft', 'yd', 'mi'].includes(artifact.displayUnit) ? artifact.displayUnit : 'ft';
                const midpoint = Cesium.Cartesian3.midpoint(startPosition, endPosition, new Cesium.Cartesian3());
                const text = `${formatDistance(distance, unit)} · ${bearingDeg.toFixed(1)}°`;
                entities.push(createMeasurementLabel(dataSourceRef.current, midpoint, text));
            }
            return entities;
        }
        catch (_a) {
            return [];
        }
    }, [getParsedGeometry]);
    const renderMeasurementCircle = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            const center = Cesium.Cartesian3.fromDegrees(geometry.centerLng, geometry.centerLat);
            const radius = typeof geometry.radiusMeters === 'number' ? geometry.radiusMeters : 0;
            const lineWidth = typeof geometry.lineWidth === 'number' ? geometry.lineWidth : 3;
            // Fill ellipse + polyline outline. No center dot, no radius label
            // — same idiom as the live tool.
            const fillOpacity = typeof geometry.fillOpacity === 'number' ? geometry.fillOpacity : undefined;
            const outlineColor = typeof geometry.outlineColor === 'string' ? geometry.outlineColor : artifact.color;
            const circleEntity = createServerCircleShape(dataSourceRef.current, center, radius, artifact.color, fillOpacity);
            const outlineEntity = createServerCircleOutline(dataSourceRef.current, center, radius, outlineColor, lineWidth, viewer !== null && viewer !== void 0 ? viewer : undefined);
            const entities = [circleEntity, outlineEntity];
            const showLabel = typeof geometry.showLabel === 'boolean' ? geometry.showLabel : false;
            if (showLabel) {
                const distanceUnit = ['m', 'km', 'ft', 'yd', 'mi'].includes(artifact.displayUnit) ? artifact.displayUnit : 'ft';
                const areaUnit = ['sqm', 'sqft', 'acres', 'hectares'].includes(geometry.areaUnit) ? geometry.areaUnit : 'acres';
                const area = Math.PI * radius * radius;
                const text = `Radius ${formatDistance(radius, distanceUnit)} · Area ${formatAreaFromSqMeters(area, areaUnit)}`;
                entities.push(createServerCircleLabel(dataSourceRef.current, center, text));
            }
            return entities;
        }
        catch (_a) {
            return [];
        }
    }, [getParsedGeometry]);
    const renderPath = useCallback((artifact) => {
        var _a;
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            const positions = geometry.points.map((p) => Cesium.Cartesian3.fromDegrees(p.lng, p.lat));
            const entities = [];
            if (positions.length >= 2) {
                const lineWidth = typeof geometry.lineWidth === 'number' ? geometry.lineWidth : undefined;
                const lineEntity = createPathLine(dataSourceRef.current, positions, artifact.color, 1.0, lineWidth, viewer !== null && viewer !== void 0 ? viewer : undefined);
                entities.push(lineEntity);
            }
            // If the path was saved with a buffer, paint the buffer band too. The
            // polygons are persisted in geometry.buffer.polygons (see promotion).
            // Mirror the live tool's per-polygon paint (usePaths.applyBuffer) so the
            // saved visual is identical to the in-progress one.
            const bufferPolys = Array.isArray((_a = geometry.buffer) === null || _a === void 0 ? void 0 : _a.polygons)
                ? geometry.buffer.polygons
                : [];
            if (bufferPolys.length > 0 && dataSourceRef.current) {
                const color = Cesium.Color.fromCssColorString(artifact.color);
                const fillMaterial = new Cesium.ColorMaterialProperty(color.withAlpha(0.3));
                const outlineColor = new Cesium.ConstantProperty(color.withAlpha(0.8));
                for (const poly of bufferPolys) {
                    if (!(poly === null || poly === void 0 ? void 0 : poly.exterior) || poly.exterior.length < 3)
                        continue;
                    const bufferEntity = dataSourceRef.current.entities.add({
                        polygon: {
                            hierarchy: buildBufferHierarchy(poly),
                            material: fillMaterial,
                            outline: true,
                            outlineColor,
                            outlineWidth: 2,
                            heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                        }
                    });
                    entities.push(bufferEntity);
                }
            }
            return entities;
        }
        catch (_b) {
            return [];
        }
    }, [getParsedGeometry]);
    const renderPolygon = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            const positions = geometry.vertices.map((v) => Cesium.Cartesian3.fromDegrees(v.lng, v.lat));
            const entities = [];
            if (positions.length >= 3) {
                const fillOpacity = typeof geometry.fillOpacity === 'number' ? geometry.fillOpacity : DEFAULT_POLYGON_FILL_OPACITY;
                const outlineColor = typeof geometry.outlineColor === 'string' ? geometry.outlineColor : artifact.color;
                const polygonEntity = createPolygonFill(dataSourceRef.current, positions, artifact.color, fillOpacity);
                entities.push(polygonEntity);
                const lineWidth = typeof geometry.lineWidth === 'number' ? geometry.lineWidth : undefined;
                const outlineEntity = createPolygonOutline(dataSourceRef.current, positions, outlineColor, true, 1.0, lineWidth, viewer !== null && viewer !== void 0 ? viewer : undefined);
                entities.push(outlineEntity);
            }
            return entities;
        }
        catch (_a) {
            return [];
        }
    }, [getParsedGeometry]);
    const renderContourHighlight = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            const positions = geometry.coordinates.map((c) => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
            const color = Cesium.Color.fromCssColorString(artifact.color);
            // Saved partial highlights store the full source coordinates plus a
            // normalized range — render only the highlighted slice (full line
            // when whole).
            const startFraction = typeof geometry.start_fraction === 'number' ? geometry.start_fraction : 0;
            const endFraction = typeof geometry.end_fraction === 'number' ? geometry.end_fraction : 1;
            const renderedPositions = slicePolylineByFraction(positions, startFraction, endFraction);
            const lineWidth = typeof geometry.line_width === 'number' ? geometry.line_width : 5;
            // Match the base contour layers' terrain mode so the highlight drapes
            // with them when 3D terrain is on (the VECTOR_LAYER_TAG below opts this
            // entity into the terrain-toggle re-sync). depthFailMaterial keeps it
            // on top of the unclamped base lines while terrain is off; zIndex does
            // the same job while clamped.
            const entity = dataSourceRef.current.entities.add({
                polyline: {
                    positions: renderedPositions,
                    material: color,
                    depthFailMaterial: color,
                    width: lineWidth,
                    clampToGround: viewer ? isTerrainOn(viewer) : false,
                    arcType: Cesium.ArcType.GEODESIC,
                    zIndex: CONTOUR_HIGHLIGHT_ZINDEX
                }
            });
            entity[VECTOR_LAYER_TAG] = true;
            return [entity];
        }
        catch (_a) {
            return [];
        }
    }, [getParsedGeometry, viewer]);
    const renderStreamSegment = useCallback((lineCoordinates, bufferPolys, color) => {
        if (!dataSourceRef.current)
            return [];
        const entities = [];
        const linePositions = lineCoordinates.map((c) => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
        const lineEntity = dataSourceRef.current.entities.add({
            polyline: {
                positions: linePositions,
                material: color,
                width: 5,
                clampToGround: true
            }
        });
        entities.push(lineEntity);
        const fillColor = color.withAlpha(0.3);
        const outlineColor = color.withAlpha(0.8);
        for (const poly of bufferPolys) {
            if (!poly.exterior || poly.exterior.length < 3)
                continue;
            const bufferEntity = dataSourceRef.current.entities.add({
                polygon: {
                    hierarchy: buildBufferHierarchy(poly),
                    material: fillColor,
                    outline: true,
                    outlineColor,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                }
            });
            entities.push(bufferEntity);
        }
        return entities;
    }, []);
    const mergeAndRenderBuffers = useCallback((bufferPolys, color) => {
        var _a;
        if (!dataSourceRef.current || bufferPolys.length === 0)
            return [];
        const closeRing = (coords) => {
            const ring = coords.map(c => [c.lng, c.lat]);
            if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                ring.push([ring[0][0], ring[0][1]]);
            }
            return ring;
        };
        const features = [];
        for (const poly of bufferPolys) {
            if (!poly.exterior || poly.exterior.length < 3)
                continue;
            const rings = [closeRing(poly.exterior)];
            for (const hole of (_a = poly.holes) !== null && _a !== void 0 ? _a : []) {
                if (hole && hole.length >= 3)
                    rings.push(closeRing(hole));
            }
            try {
                features.push(turfPolygon(rings));
            }
            catch (_b) {
                // skip invalid polygon
            }
        }
        if (features.length === 0)
            return [];
        let mergedPolygon = null;
        try {
            mergedPolygon = features.length === 1
                ? features[0]
                : union(featureCollection(features));
        }
        catch (_c) {
            return [];
        }
        if (!mergedPolygon)
            return [];
        const fillColor = color.withAlpha(0.3);
        const outlineColor = color.withAlpha(0.8);
        const entities = [];
        const createPositions = (coords) => coords.map(c => Cesium.Cartesian3.fromDegrees(c[0], c[1]));
        const createHierarchy = (polyCoords) => {
            const outerRing = createPositions(polyCoords[0]);
            const holes = polyCoords.slice(1).map(hole => new Cesium.PolygonHierarchy(createPositions(hole)));
            return new Cesium.PolygonHierarchy(outerRing, holes);
        };
        const addPolygonEntity = (polyCoords) => {
            const hierarchy = createHierarchy(polyCoords);
            const entity = dataSourceRef.current.entities.add({
                polygon: {
                    hierarchy,
                    material: new Cesium.ColorMaterialProperty(fillColor),
                    outline: true,
                    outlineColor,
                    outlineWidth: 2,
                    heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
                }
            });
            entities.push(entity);
        };
        if (mergedPolygon.geometry.type === 'Polygon') {
            addPolygonEntity(mergedPolygon.geometry.coordinates);
        }
        else if (mergedPolygon.geometry.type === 'MultiPolygon') {
            for (const polyCoords of mergedPolygon.geometry.coordinates) {
                addPolygonEntity(polyCoords);
            }
        }
        return entities;
    }, []);
    const renderStreamHighlight = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            const geometry = getParsedGeometry(artifact);
            const color = Cesium.Color.fromCssColorString(artifact.color);
            // Coerce legacy flat-ring buffer_polygon into the new polygon-with-holes
            // shape so old saved artifacts still render (without holes).
            const readBufferPolygons = (src) => {
                if (Array.isArray(src === null || src === void 0 ? void 0 : src.bufferPolygons))
                    return src.bufferPolygons;
                if (Array.isArray(src === null || src === void 0 ? void 0 : src.buffer_polygons))
                    return src.buffer_polygons;
                const legacy = Array.isArray(src === null || src === void 0 ? void 0 : src.bufferPolygon)
                    ? src.bufferPolygon
                    : (Array.isArray(src === null || src === void 0 ? void 0 : src.buffer_polygon) ? src.buffer_polygon : null);
                if (legacy && legacy.length >= 3)
                    return [{ exterior: legacy, holes: [] }];
                return [];
            };
            // Multi-segment stream highlight — render lines individually, merge buffers
            if (geometry.is_multi_segment && geometry.segments) {
                const entities = [];
                const allBuffers = [];
                for (const seg of geometry.segments) {
                    // Render each line segment
                    const lineCoords = seg.lineCoordinates || [];
                    if (lineCoords.length > 0) {
                        const linePositions = lineCoords.map((c) => Cesium.Cartesian3.fromDegrees(c.lng, c.lat));
                        const lineEntity = dataSourceRef.current.entities.add({
                            polyline: {
                                positions: linePositions,
                                material: color,
                                width: 5,
                                clampToGround: true
                            }
                        });
                        entities.push(lineEntity);
                    }
                    // Collect buffers for merging
                    allBuffers.push(...readBufferPolygons(seg));
                }
                // Merge all buffers into a continuous zone
                entities.push(...mergeAndRenderBuffers(allBuffers, color));
                return entities;
            }
            // Single-segment stream highlight
            return renderStreamSegment(geometry.line_coordinates || [], readBufferPolygons(geometry), color);
        }
        catch (_a) {
            return [];
        }
    }, [getParsedGeometry, renderStreamSegment, mergeAndRenderBuffers]);
    // Sketch: a freehand canvas saved as one artifact whose geometry holds
    // many strokes. Each stroke renders as its own terrain-clamped polyline
    // at its stored colour / width / opacity — the same primitive as a path,
    // just one per stroke.
    const renderSketch = useCallback((artifact) => {
        if (!dataSourceRef.current)
            return [];
        try {
            // parseSketchGeometry, not getParsedGeometry: the shared parser is what
            // clamps opacity and drops undrawable strokes, and a stroke opacity
            // outside 0..1 would otherwise register a base the artifact slider can
            // never scale back down.
            const { strokes } = parseSketchGeometry(artifact.geometryJson);
            const entities = [];
            for (const stroke of strokes) {
                const positions = stroke.points.map(p => Cesium.Cartesian3.fromDegrees(p.lng, p.lat));
                const entity = createPathLine(dataSourceRef.current, positions, stroke.color, stroke.opacity, stroke.width, viewer !== null && viewer !== void 0 ? viewer : undefined);
                // The artifact-opacity effect runs right after this and multiplies
                // against the base, so register before returning.
                registerLineBaseOpacity(entity, stroke.opacity);
                entities.push(entity);
            }
            return entities;
        }
        catch (_a) {
            return [];
        }
    }, [viewer]);
    const renderArtifact = useCallback((artifact) => {
        switch (artifact.artifactType) {
            case 'placemark':
                return renderPlacemark(artifact);
            case 'label':
                return renderLabel(artifact);
            case 'measurement':
                return renderMeasurement(artifact);
            case 'measurement_circle':
                return renderMeasurementCircle(artifact);
            case 'path':
                return renderPath(artifact);
            case 'polygon':
                return renderPolygon(artifact);
            case 'contour_highlight':
                return renderContourHighlight(artifact);
            case 'stream_highlight':
                return renderStreamHighlight(artifact);
            case 'sketch':
                return renderSketch(artifact);
            default:
                return [];
        }
    }, [renderPlacemark, renderLabel, renderMeasurement, renderMeasurementCircle, renderPath, renderPolygon, renderContourHighlight, renderStreamHighlight, renderSketch]);
    useEffect(() => {
        if (!dataSourceRef.current || !viewer || viewer.isDestroyed())
            return;
        const currentArtifactIds = new Set();
        folders.forEach(folder => {
            folder.artifacts.forEach(artifact => {
                currentArtifactIds.add(artifact.id);
            });
        });
        renderedArtifactsRef.current.forEach((rendered, artifactId) => {
            if (!currentArtifactIds.has(artifactId)) {
                rendered.entities.forEach(entity => {
                    if (dataSourceRef.current) {
                        dataSourceRef.current.entities.remove(entity);
                    }
                    entityToArtifactRef.current.delete(entity);
                });
                renderedArtifactsRef.current.delete(artifactId);
                parsedGeometryRef.current.delete(artifactId);
                // Saved label overlay data tracks rendered entities; evict the
                // matching entry so the DOM overlay drops it too.
                setSavedLabels(prev => {
                    if (!prev.has(artifactId))
                        return prev;
                    const next = new Map(prev);
                    next.delete(artifactId);
                    return next;
                });
            }
        });
        folders.forEach(folder => {
            folder.artifacts.forEach(artifact => {
                const rendered = renderedArtifactsRef.current.get(artifact.id);
                const cachedGeometry = parsedGeometryRef.current.get(artifact.id);
                const cachedArtifact = rendered ? entityToArtifactRef.current.get(rendered.entities[0]) : undefined;
                const geometryChanged = !cachedGeometry || cachedGeometry.json !== artifact.geometryJson;
                const colorChanged = cachedArtifact ? cachedArtifact.color !== artifact.color : false;
                const nameChanged = cachedArtifact ? cachedArtifact.name !== artifact.name : false;
                const iconChanged = cachedArtifact ? cachedArtifact.icon !== artifact.icon : false;
                const showLabelChanged = cachedArtifact ? cachedArtifact.showLabel !== artifact.showLabel : false;
                const isStale = rendered && (geometryChanged || colorChanged || nameChanged || iconChanged || showLabelChanged);
                if (rendered && isStale) {
                    rendered.entities.forEach(entity => {
                        if (dataSourceRef.current) {
                            dataSourceRef.current.entities.remove(entity);
                        }
                        entityToArtifactRef.current.delete(entity);
                    });
                    renderedArtifactsRef.current.delete(artifact.id);
                    parsedGeometryRef.current.delete(artifact.id);
                    // Re-render will repopulate savedLabels with the fresh data
                    // below; clear the stale entry so the overlay doesn't flash.
                    if (artifact.artifactType === 'label') {
                        setSavedLabels(prev => {
                            if (!prev.has(artifact.id))
                                return prev;
                            const next = new Map(prev);
                            next.delete(artifact.id);
                            return next;
                        });
                    }
                }
                if (!renderedArtifactsRef.current.has(artifact.id)) {
                    const entities = renderArtifact(artifact);
                    if (entities.length > 0) {
                        renderedArtifactsRef.current.set(artifact.id, {
                            artifactId: artifact.id,
                            entities
                        });
                        entities.forEach(entity => {
                            entityToArtifactRef.current.set(entity, artifact);
                        });
                    }
                }
            });
        });
    }, [folders, viewer, renderArtifact]);
    useEffect(() => {
        // Build a lookup of artifact metadata so we can respect per-placemark
        // `showLabel` when deciding whether a label *entity* should be visible.
        // Without this, the generic `entity.show = baseVisible` write below
        // overwrites the labelEntity.show = false applied at render time.
        const artifactById = new Map();
        folders.forEach(folder => {
            folder.artifacts.forEach(artifact => {
                artifactById.set(artifact.id, artifact);
            });
        });
        const applyVisibility = (rendered, artifactId, baseVisibleRaw) => {
            const artifact = artifactById.get(artifactId);
            const showLabelAllowed = (artifact === null || artifact === void 0 ? void 0 : artifact.showLabel) !== false;
            // An editable copy is on screen for this artifact — keep the server
            // render hidden without disturbing the user's visibility toggle.
            const baseVisible = baseVisibleRaw && !suppressedRef.current.has(artifactId);
            rendered.entities.forEach(entity => {
                // Label entities (those with a `label` graphic) AND in the per-
                // placemark showLabel preference; everything else is gated on
                // baseVisible alone.
                if (entity.label) {
                    entity.show = baseVisible && showLabelAllowed;
                }
                else {
                    entity.show = baseVisible;
                }
            });
        };
        if (derivedVisibility) {
            renderedArtifactsRef.current.forEach((rendered, artifactId) => {
                var _a;
                const vis = derivedVisibility.get(artifactId);
                applyVisibility(rendered, artifactId, (_a = vis === null || vis === void 0 ? void 0 : vis.visible) !== null && _a !== void 0 ? _a : false);
            });
        }
        else {
            const artifactFolderMap = new Map();
            folders.forEach(folder => {
                folder.artifacts.forEach(artifact => {
                    artifactFolderMap.set(artifact.id, folder.id);
                });
            });
            renderedArtifactsRef.current.forEach((rendered, artifactId) => {
                const folderId = artifactFolderMap.get(artifactId);
                const folderVisible = folderId ? (folderVisibility[folderId] !== false) : true;
                const artifactVisible = artifactVisibility[artifactId] === true;
                applyVisibility(rendered, artifactId, folderVisible && artifactVisible);
            });
        }
    }, [derivedVisibility, artifactVisibility, folderVisibility, folders, suppressedArtifactIds]);
    useEffect(() => {
        const artifactMap = new Map();
        folders.forEach(folder => {
            folder.artifacts.forEach(artifact => {
                artifactMap.set(artifact.id, artifact);
            });
        });
        renderedArtifactsRef.current.forEach((rendered, artifactId) => {
            var _a;
            const opacity = (_a = artifactOpacity[artifactId]) !== null && _a !== void 0 ? _a : 1.0;
            const artifact = artifactMap.get(artifactId);
            const color = (artifact === null || artifact === void 0 ? void 0 : artifact.color) || '#ffffff';
            rendered.entities.forEach(entity => {
                if (entity.billboard) {
                    updateBillboardOpacity(entity, color, opacity, artifact === null || artifact === void 0 ? void 0 : artifact.icon, viewer !== null && viewer !== void 0 ? viewer : undefined);
                }
                if (entity.label) {
                    updateLabelOpacity(entity, opacity);
                }
                if (entity.point) {
                    updatePointOpacity(entity, color, opacity);
                }
                if (entity.polyline) {
                    if ((artifact === null || artifact === void 0 ? void 0 : artifact.artifactType) === 'sketch') {
                        updateLineAlpha(entity, opacity);
                    }
                    else {
                        let lineColor = color;
                        if (artifact && artifact.artifactType === 'polygon') {
                            try {
                                const geometry = getParsedGeometry(artifact);
                                if (typeof geometry.outlineColor === 'string')
                                    lineColor = geometry.outlineColor;
                            }
                            catch ( /* fall back to artifact.color */_a) { /* fall back to artifact.color */ }
                        }
                        updateLineOpacity(entity, lineColor, opacity);
                    }
                }
                if (entity.polygon) {
                    let baseFillAlpha = DEFAULT_POLYGON_FILL_OPACITY;
                    if (artifact && artifact.artifactType === 'polygon') {
                        try {
                            const geometry = getParsedGeometry(artifact);
                            if (typeof geometry.fillOpacity === 'number')
                                baseFillAlpha = geometry.fillOpacity;
                        }
                        catch ( /* fall back to default */_b) { /* fall back to default */ }
                    }
                    updatePolygonFillOpacity(entity, color, opacity, baseFillAlpha);
                }
            });
        });
    }, [artifactOpacity, folders, viewer, getParsedGeometry]);
    const onPathArtifactClickRef = useRef(onPathArtifactClick);
    useEffect(() => { onPathArtifactClickRef.current = onPathArtifactClick; }, [onPathArtifactClick]);
    const onPolygonArtifactClickRef = useRef(onPolygonArtifactClick);
    useEffect(() => { onPolygonArtifactClickRef.current = onPolygonArtifactClick; }, [onPolygonArtifactClick]);
    const onStreamArtifactClickRef = useRef(onStreamArtifactClick);
    useEffect(() => { onStreamArtifactClickRef.current = onStreamArtifactClick; }, [onStreamArtifactClick]);
    const checkServerArtifactClick = useCallback((event) => {
        if (!viewer || viewer.isDestroyed())
            return false;
        const pickedObject = viewer.scene.pick(event.position);
        if (!(pickedObject === null || pickedObject === void 0 ? void 0 : pickedObject.id))
            return false;
        const entity = pickedObject.id;
        const artifact = entityToArtifactRef.current.get(entity);
        if (artifact) {
            // Per-type divert: paths and streams route into their own dialogs in
            // view mode instead of the generic ServerArtifactDialog. Other types
            // fall through to the default.
            if (artifact.artifactType === 'path' && onPathArtifactClickRef.current) {
                onPathArtifactClickRef.current(artifact);
                return true;
            }
            if (artifact.artifactType === 'polygon' && onPolygonArtifactClickRef.current) {
                onPolygonArtifactClickRef.current(artifact);
                return true;
            }
            if (artifact.artifactType === 'stream_highlight' && onStreamArtifactClickRef.current) {
                onStreamArtifactClickRef.current(artifact);
                return true;
            }
            setSelectedArtifact({
                artifact,
                screenPosition: { x: event.position.x, y: event.position.y }
            });
            return true;
        }
        return false;
    }, [viewer]);
    // Dismiss only when the selected artifact actually disappears (deleted).
    // The old behaviour also auto-dismissed when the artifact became hidden,
    // which made sense when the only way to open this dialog was clicking the
    // artifact on the map — but the panel's ⓘ button opens it regardless of
    // visibility, so a hidden artifact must be allowed to keep its dialog open.
    useEffect(() => {
        if (!selectedArtifact)
            return;
        const exists = folders.some(f => f.artifacts.some(a => a.id === selectedArtifact.artifact.id));
        if (!exists)
            setSelectedArtifact(null);
    }, [selectedArtifact, folders]);
    // Keep the selected artifact reference in sync with `folders` so downstream
    // actions (Edit button, etc.) use the latest data after an in-dialog rename
    // or other mutation. Without this, the dialog's onChangeName updates the
    // panel + server but `selectedArtifact.artifact` keeps the snapshot taken
    // at click time, and clicking Edit loads stale fields into the edit tool.
    useEffect(() => {
        if (!selectedArtifact)
            return;
        const fresh = folders
            .flatMap(f => f.artifacts)
            .find(a => a.id === selectedArtifact.artifact.id);
        if (fresh && fresh !== selectedArtifact.artifact) {
            setSelectedArtifact(prev => prev ? { ...prev, artifact: fresh } : prev);
        }
    }, [folders, selectedArtifact]);
    const dismissSelection = useCallback(() => {
        setSelectedArtifact(null);
    }, []);
    // Programmatic counterpart to the on-map click path. Used by the panel's
    // info icon / right-click "Get Info" so panel-driven and map-driven
    // selection both feed the same PathOptionsDialog (and equivalents).
    const selectArtifact = useCallback((artifact, screenPosition) => {
        setSelectedArtifact({ artifact, screenPosition });
    }, []);
    const popOutArtifact = useCallback((artifact, position, segmentIndex = null, width) => {
        setPoppedOutArtifacts(prev => {
            if (prev.some(p => { var _a; return p.artifact.id === artifact.id && ((_a = p.segmentIndex) !== null && _a !== void 0 ? _a : null) === segmentIndex; })) {
                return prev;
            }
            return [...prev, { artifact, position, segmentIndex, width }];
        });
        setSelectedArtifact(null);
    }, []);
    const closePopOutArtifact = useCallback((artifactId, segmentIndex = null) => {
        setPoppedOutArtifacts(prev => prev.filter(p => { var _a; return !(p.artifact.id === artifactId && ((_a = p.segmentIndex) !== null && _a !== void 0 ? _a : null) === segmentIndex); }));
    }, []);
    const highlightEntityRef = useRef(null);
    const highlightPathSegment = useCallback((artifactId, segmentIndex) => {
        if (!dataSourceRef.current || !viewer || viewer.isDestroyed())
            return;
        if (highlightEntityRef.current) {
            dataSourceRef.current.entities.remove(highlightEntityRef.current);
            highlightEntityRef.current = null;
        }
        if (segmentIndex === null)
            return;
        let artifact = null;
        for (const folder of folders) {
            const found = folder.artifacts.find(a => a.id === artifactId);
            if (found) {
                artifact = found;
                break;
            }
        }
        if (!artifact || artifact.artifactType !== 'path')
            return;
        try {
            const geometry = getParsedGeometry(artifact);
            const points = geometry.points;
            if (!points || segmentIndex >= points.length - 1)
                return;
            const start = points[segmentIndex];
            const end = points[segmentIndex + 1];
            const positions = [
                Cesium.Cartesian3.fromDegrees(start.lng, start.lat),
                Cesium.Cartesian3.fromDegrees(end.lng, end.lat)
            ];
            highlightEntityRef.current = createGlowHighlight(dataSourceRef.current, positions, artifact.color);
        }
        catch (_a) {
            // Ignore parsing errors
        }
    }, [viewer, folders, getParsedGeometry]);
    const edgeHighlightEntityRef = useRef(null);
    const highlightPolygonEdge = useCallback((artifactId, edgeIndex) => {
        if (!dataSourceRef.current || !viewer || viewer.isDestroyed())
            return;
        if (edgeHighlightEntityRef.current) {
            dataSourceRef.current.entities.remove(edgeHighlightEntityRef.current);
            edgeHighlightEntityRef.current = null;
        }
        if (edgeIndex === null)
            return;
        let artifact = null;
        for (const folder of folders) {
            const found = folder.artifacts.find(a => a.id === artifactId);
            if (found) {
                artifact = found;
                break;
            }
        }
        if (!artifact || artifact.artifactType !== 'polygon')
            return;
        try {
            const geometry = getParsedGeometry(artifact);
            const vertices = geometry.vertices;
            if (!vertices || edgeIndex >= vertices.length)
                return;
            const start = vertices[edgeIndex];
            const end = vertices[(edgeIndex + 1) % vertices.length];
            const positions = [
                Cesium.Cartesian3.fromDegrees(start.lng, start.lat),
                Cesium.Cartesian3.fromDegrees(end.lng, end.lat)
            ];
            edgeHighlightEntityRef.current = createGlowHighlight(dataSourceRef.current, positions, artifact.color);
        }
        catch (_a) {
            // Ignore parsing errors
        }
    }, [viewer, folders, getParsedGeometry]);
    const flyToArtifact = useCallback((artifactId) => {
        var _a, _b;
        if (!viewer || viewer.isDestroyed())
            return;
        const rendered = renderedArtifactsRef.current.get(artifactId);
        if (!rendered || rendered.entities.length === 0)
            return;
        const positions = [];
        for (const entity of rendered.entities) {
            if (entity.position) {
                const pos = entity.position.getValue(Cesium.JulianDate.now());
                if (pos)
                    positions.push(pos);
            }
            if (entity.polyline) {
                const positionsProp = (_a = entity.polyline.positions) === null || _a === void 0 ? void 0 : _a.getValue(Cesium.JulianDate.now());
                if (positionsProp)
                    positions.push(...positionsProp);
            }
            if ((_b = entity.polygon) === null || _b === void 0 ? void 0 : _b.hierarchy) {
                const hierarchy = entity.polygon.hierarchy.getValue(Cesium.JulianDate.now());
                if (hierarchy === null || hierarchy === void 0 ? void 0 : hierarchy.positions)
                    positions.push(...hierarchy.positions);
            }
        }
        if (positions.length === 0)
            return;
        const boundingSphere = Cesium.BoundingSphere.fromPoints(positions);
        viewer.camera.flyToBoundingSphere(boundingSphere, {
            duration: 1.0,
            offset: new Cesium.HeadingPitchRange(0, -Cesium.Math.PI_OVER_TWO, 0),
        });
    }, [viewer]);
    return {
        dataSource: dataSourceRef.current,
        selectedArtifact,
        poppedOutArtifacts,
        savedLabels,
        checkServerArtifactClick,
        dismissSelection,
        selectArtifact,
        popOutArtifact,
        closePopOutArtifact,
        highlightPathSegment,
        highlightPolygonEdge,
        flyToArtifact
    };
};
